export const DOSSIER_LAYOUT_CHANGE_EVENT = 'entangled-vega:dossier-layout-change';

export const DOSSIER_EDGE_GAP = 12;
export const DOSSIER_MIN_WIDTH = 320;
export const DOSSIER_MIN_HEIGHT = 260;
export const DOSSIER_SHEET_STATES = ['peek', 'half', 'full'] as const;

export type DossierSheetState = (typeof DOSSIER_SHEET_STATES)[number];

export type DossierRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type DossierViewport = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type DossierWorkspaceState = {
  mode: 'docked' | 'floating';
  pinned: boolean;
  minimized: boolean;
  rect: DossierRect | null;
  sheetState: DossierSheetState;
  sheetStateExplicit: boolean;
};

export const DEFAULT_DOSSIER_WORKSPACE: DossierWorkspaceState = {
  mode: 'docked',
  pinned: false,
  minimized: false,
  rect: null,
  sheetState: 'half',
  sheetStateExplicit: false,
};

export function initialDossierWorkspaceForView(view: 'graph' | 'list'): DossierWorkspaceState {
  return {
    ...DEFAULT_DOSSIER_WORKSPACE,
    sheetState: view === 'list' ? 'peek' : 'half',
  };
}

export function defaultDossierSheetState(
  workspace: DossierWorkspaceState,
  state: DossierSheetState,
): DossierWorkspaceState {
  return workspace.sheetStateExplicit || workspace.sheetState === state
    ? workspace
    : { ...workspace, sheetState: state };
}

export function preferDossierSheetState(
  workspace: DossierWorkspaceState,
  state: DossierSheetState,
): DossierWorkspaceState {
  return workspace.sheetState === state && workspace.sheetStateExplicit
    ? workspace
    : { ...workspace, sheetState: state, sheetStateExplicit: true };
}

export function adjacentDossierSheetState(
  state: DossierSheetState,
  direction: 'expand' | 'collapse',
): DossierSheetState {
  const index = DOSSIER_SHEET_STATES.indexOf(state);
  const nextIndex = direction === 'expand' ? index + 1 : index - 1;
  return DOSSIER_SHEET_STATES[Math.min(Math.max(nextIndex, 0), DOSSIER_SHEET_STATES.length - 1)]!;
}

export function isNarrowDossierViewport(viewport: DossierViewport): boolean {
  const width = finite(viewport.width, 1);
  const height = finite(viewport.height, 1);
  return width <= 720 || (height <= 520 && width <= 980);
}

export function dossierSheetHeight(state: DossierSheetState, availableHeight: number): number {
  const available = Math.max(1, finite(availableHeight, 1));
  if (state === 'peek') {
    return Math.min(104, available);
  }
  if (state === 'full') {
    return available;
  }
  return Math.min(available, Math.max(180, available * 0.52));
}

function finite(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

function availableViewport(viewport: DossierViewport) {
  const left = finite(viewport.left, 0) + DOSSIER_EDGE_GAP;
  const top = finite(viewport.top, 0) + DOSSIER_EDGE_GAP;
  const width = Math.max(1, finite(viewport.width, 1) - DOSSIER_EDGE_GAP * 2);
  const height = Math.max(1, finite(viewport.height, 1) - DOSSIER_EDGE_GAP * 2);
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  };
}

export function clampDossierRect(rect: DossierRect, viewport: DossierViewport): DossierRect {
  const available = availableViewport(viewport);
  const minimumWidth = Math.min(DOSSIER_MIN_WIDTH, available.width);
  const minimumHeight = Math.min(DOSSIER_MIN_HEIGHT, available.height);
  const width = clamp(finite(rect.width, minimumWidth), minimumWidth, available.width);
  const height = clamp(finite(rect.height, minimumHeight), minimumHeight, available.height);
  return {
    left: clamp(finite(rect.left, available.left), available.left, available.right - width),
    top: clamp(finite(rect.top, available.top), available.top, available.bottom - height),
    width,
    height,
  };
}

export function defaultFloatingDossierRect(viewport: DossierViewport): DossierRect {
  const available = availableViewport(viewport);
  const width =
    available.width < 560
      ? available.width
      : clamp(available.width * 0.48, 560, Math.min(720, available.width));
  const height =
    available.height < 420
      ? available.height
      : clamp(available.height * 0.72, 420, Math.min(720, available.height));
  return clampDossierRect(
    {
      left: available.right - width,
      top: available.top + (available.height - height) / 2,
      width,
      height,
    },
    viewport,
  );
}

export function moveDossierRect(
  rect: DossierRect,
  deltaX: number,
  deltaY: number,
  viewport: DossierViewport,
): DossierRect {
  return clampDossierRect(
    {
      ...rect,
      left: rect.left + finite(deltaX, 0),
      top: rect.top + finite(deltaY, 0),
    },
    viewport,
  );
}

export function resizeDossierRect(
  rect: DossierRect,
  deltaWidth: number,
  deltaHeight: number,
  viewport: DossierViewport,
): DossierRect {
  const available = availableViewport(viewport);
  const current = clampDossierRect(rect, viewport);
  const maximumWidth = Math.max(1, available.right - current.left);
  const maximumHeight = Math.max(1, available.bottom - current.top);
  return {
    ...current,
    width: clamp(
      current.width + finite(deltaWidth, 0),
      Math.min(DOSSIER_MIN_WIDTH, maximumWidth),
      maximumWidth,
    ),
    height: clamp(
      current.height + finite(deltaHeight, 0),
      Math.min(DOSSIER_MIN_HEIGHT, maximumHeight),
      maximumHeight,
    ),
  };
}

export function dossierRectsEqual(first: DossierRect | null, second: DossierRect | null): boolean {
  if (first === second) return true;
  if (first === null || second === null) return false;
  return (
    first.left === second.left &&
    first.top === second.top &&
    first.width === second.width &&
    first.height === second.height
  );
}
